import React, { useState, useContext, useEffect, useMemo } from "react";
import apiClient from "../../ApiEndpoint/apiClient";
import Spinner from "../Spinner/spinner.js";
import apiUrl from "../../ApiEndpoint/config";
import { Tabs, Select, Collapse, Spin } from "antd";
import { RegisterIdContext } from "../../hooks/RegisterIdContext";
import { from, of } from "rxjs";
import {
    debounceTime,
    switchMap,
    catchError,
    filter,
} from "rxjs/operators";


const { TabPane } = Tabs;
const { Panel } = Collapse;


const ProjectDetails = () => {
    const [projectDetail, setProjectDetail] = useState({});
    const [projectDetailLoading, setProjectDetailLoading] = useState(false);
    const { projectId } = useContext(RegisterIdContext);
    const [activeTabKey, setActiveTabKey] = useState("1");
    const [selectedPlant, setSelectedPlant] = useState(null);
    const { refineSearchPlantID } = useContext(RegisterIdContext);
    const [projectObjectData, setProjectObjectData] = useState({})



    //Project Detail api integration
    useEffect(() => {
        if (!projectId) {
            setProjectDetail([]); // clear previous details
            return;
        }
        setActiveTabKey("2");
        const controller = new AbortController();
        const signal = controller.signal;

        const projectId$ = of(projectId).pipe(
            debounceTime(1000),
            filter((id) => !!id), // Guard against falsy IDs
            switchMap((id) =>
                from(
                    apiClient.get(`${apiUrl}/projects-detail`, {
                        params: { projectId: id },
                        signal,
                    })
                ).pipe(
                    catchError((error) => {
                        console.error("Error fetching project detail:", error);
                        setProjectDetail([]);
                        return of(null);
                    })
                )
            )
        );

        setProjectDetailLoading(true);

        const subscription = projectId$.subscribe((response) => {
            if (response?.data) {
                setProjectDetail(response.data);
                setProjectObjectData(response.data.projects[0])
            }
            setProjectDetailLoading(false);
        });

        return () => {
            subscription.unsubscribe();
            controller.abort();
        };
    }, [projectId]);




    //plant api integration
    useEffect(() => {
        if (!refineSearchPlantID) return;

        const fetchPlantDetails = async () => {
            try {
                const { data } = await apiClient.get(`${apiUrl}/plants-detail`, {
                    params: { plantId: refineSearchPlantID },
                });

                const plant = data?.plants?.[0] || null;
                setSelectedPlant(plant);
            } catch (error) {
                console.error("Error fetching plant details:", error);
                setSelectedPlant(null);
            }
        };

        fetchPlantDetails();
    }, [refineSearchPlantID]);


    //Facility Sumary
    const [message, setMessage] = useState("");
    const [citations, setCitations] = useState([]);
    const [loading, setLoading] = useState(false);
    const selectedProject = projectObjectData;


    useEffect(() => {


        const fetchSummary = async () => {
            try {
                const params = {
                    plant_id: selectedProject?.plantId,
                    plant_name: selectedProject?.plantName,
                    group_name: selectedProject?.plantParentName,
                    plant_state: selectedProject?.plantPhysicalAddress?.stateName,
                    plant_city: selectedProject?.plantPhysicalAddress?.city,
                };

                // prevent API call if any of the required values are missing
                if (!params.plant_id || !params.plant_name || !params.group_name || !params.plant_state || !params.plant_city) {
                    return;
                }

                const queryString = new URLSearchParams(params).toString();

                const res = await apiClient.get(`${apiUrl}/facility_summary?${queryString}`);

                setMessage(res.data?.Message || "");
                setCitations(res.data?.Citations || []);
            } catch (err) {
                console.error("Error fetching facility sustainability:", err);
                setMessage("");
                setCitations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();
    }, [projectObjectData])



    return (
        <>
            <div className="card" style={{ padding: '10px' }}>
                <div className="mt-2">
                    <Tabs defaultActiveKey="1" activeKey={activeTabKey} onChange={(key) => setActiveTabKey(key)}>
                        <TabPane tab="Plants Details" key="1">
                            <Plants selectedPlant={selectedPlant} />
                        </TabPane>
                        <TabPane tab="Project Details" key="2">
                            <Projects projectDetail={projectDetail} projectDetailLoading={projectDetailLoading} />
                        </TabPane>
                        <TabPane tab="Sustainability Goals" key="3">
                            <SustainabilityGoals message={message}
                                citations={citations}
                                loading={loading} />
                        </TabPane>
                        <TabPane tab="Contacts" key="4">
                            <Contacts contact={selectedPlant} />
                        </TabPane>

                    </Tabs>
                </div>
            </div>
        </>

    );
};


const Projects = ({ projectDetail, projectDetailLoading }) => {


    const projectInfo = projectDetail?.projects?.[0];
    const getKeysWithValueOne = (obj) => {
        if (!obj || typeof obj !== 'object') return 'N/A';

        const keys = Object.keys(obj).filter((key) => obj[key] === 1);
        return keys.length > 0 ? keys.join(',') : 'N/A';
    };


    return (
        <>
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
                {projectDetailLoading ? (
                    <Spinner />
                ) : projectInfo && Object.keys(projectInfo).length > 0 ? (
                    <>
                        <h6 className="mt-3">Project Scope</h6>
                        <p>{projectInfo?.scope}</p>

                        <div className="detection-item">
                            <h6 className="details-title mt-3">Project Details</h6>


                            <p>
                                <span className="left col-4">Project Name:</span>
                                <span className="right col-8">{projectInfo?.projectName ?? "N/A"}</span>
                            </p>


                            <p>
                                <span className="left col-4">TIV:</span>
                                <span className="right col-8">
                                    {projectInfo?.localTiv != null
                                        ? new Intl.NumberFormat("en-US", {
                                            style: "currency",
                                            currency: "USD",
                                        }).format(projectInfo.localTiv)
                                        : "N/A"}
                                </span>

                            </p>

                            <p>
                                <span className="left col-4">Project Status:</span>
                                <span className="right col-8">{projectInfo?.projectStatusDesc ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Project SIC:</span>
                                <span className="right col-8">{projectInfo?.projectSicDesc ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Schedule:</span>
                                <span className="right col-8">{projectInfo?.schedule ?? "N/A"}</span>
                            </p>


                        </div>


                        <div className="detection-item">
                            <h6 className="details-title mt-3">Project Owner Details</h6>
                            <p>
                                <span className="left col-4">Project Owner Name:</span>
                                <span className="right col-8">{projectInfo?.projectOwnerName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Plant Name:</span>
                                <span className="right col-8">{projectInfo?.plantName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Plant SIC</span>
                                <span className="right col-8">{projectInfo?.plantSicDesc ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Plant Owner Name</span>
                                <span className="right col-8">{projectInfo?.plantOwnerName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Plant Parent Name</span>
                                <span className="right col-8">{projectInfo?.plantParentName ?? "N/A"}</span>
                            </p>

                        </div>


                        <div className="detection-item">
                            <h6 className="details-title mt-3">Plant Physical Address</h6>
                            <p>
                                <span className="left col-4">Address Line 1:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.addressLine1 ?? "N/A"}</span>
                            </p>

                            <p>
                                <span className="left col-4">City:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.city ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">County:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.countyName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">State:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.stateName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Country:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.countryName ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Postal Code:</span>
                                <span className="right col-8">{projectInfo?.plantPhysicalAddress?.postalCode ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-4">Plant Phone:</span>
                                <span className="right col-8">{projectInfo?.plantPhone ?? "N/A"}</span>
                            </p>

                            <p>
                                <span className="left col-10 mt-2">Project Evaluation Cycle timing:</span>
                                <span className="right col-2">{projectInfo?.pecTiming ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-10">Project Evaluation Activity:</span>
                                <span className="right col-2">{projectInfo?.pecActivityDesc ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-10">Construction Labor Preference:</span>
                                <span className="right col-2">
                                    {projectInfo?.constructionLaborPreference === 1
                                        ? "Yes"
                                        : projectInfo?.constructionLaborPreference === 0
                                            ? "No"
                                            : "N/A"}
                                </span>
                            </p>
                            <p>
                                <span className="left col-10">Operations Labor Preference:</span>
                                <span className="right col-2">
                                    {projectInfo?.operationsLaborPreference === 1
                                        ? "Yes"
                                        : projectInfo?.operationsLaborPreference === 0
                                            ? "No"
                                            : "N/A"}
                                </span>
                            </p>
                            <p>
                                <span className="left col-10">RFQ Date:</span>
                                <span className="right col-2">
                                    {projectInfo?.rfqYearMonth
                                        ? `${String(projectInfo.rfqYearMonth).slice(0, 4)}/${String(projectInfo.rfqYearMonth).slice(4).padStart(2, '0')}`
                                        : "N/A"}
                                </span>
                            </p>
                            <p>
                                <span className="left col-10">Kick Off Date:</span>
                                <span className="right col-2">
                                    {projectInfo?.kickoffYearMonth
                                        ? `${String(projectInfo.kickoffYearMonth).slice(0, 4)}/${String(projectInfo.kickoffYearMonth).slice(4).padStart(2, '0')}`
                                        : "N/A"}
                                </span>
                            </p>
                            <p>
                                <span className="left col-5">Project Sector:</span>
                                <span className="right col-7">{projectInfo?.projectSectorDesc ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-5">Project Key Assets:</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.energyProcessKeyNeeds ?? "N/A")}</span>
                            </p>
                            <p>
                                <span className="left col-5">Project Key Engineering needs:</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.engineeringKeyNeeds ?? "N/A")}</span>
                            </p>
                            <p>
                                <span className="left col-5">Project Key needs:</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.projectKeyNeeds ?? "N/A")}</span>
                            </p>
                            <p>
                                <span className="left col-5">Environmental Requirements</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.environmental ?? "N/A")}</span>
                            </p>
                            <p>
                                <span className="left col-5">Energy Process Key Needs</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.energyProcessKeyNeeds ?? "N/A")}</span>
                            </p>

                            <p>
                                <span className="left col-5">Energy Procurements Needed</span>
                                <span className="right col-7" style={{ wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.6' }}> {getKeysWithValueOne(projectInfo?.buy ?? "N/A")}</span>
                            </p>
                            <p>
                                <span className="left col-5">Existing Capacity:</span>
                                <span className="right col-7">{projectInfo?.installedCapacity ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-5">New Planned Capacity:</span>
                                <span className="right col-7">{projectInfo?.plannedCapacity ?? "N/A"}</span>
                            </p>
                            <p>
                                <span className="left col-5">Capacity UOM</span>
                                <span className="right col-7">{projectInfo?.uom ?? "N/A"}</span>
                            </p>

                        </div>



                        {projectInfo?.projectCompaniesSummary?.map((company, index) => {
                            const contacts = company.internalContacts || [];

                            if (contacts.length === 0) return null;

                            return (
                                <React.Fragment key={`internal-${index}`}>
                                    <h2 className="opp-main-heading">{projectInfo?.projectCompaniesSummary[0].companyInfo?.companyName} - Contact List</h2>
                                    <div key={index} className="opportunity-container">
                                        {contacts.map((contact) => (
                                            <div key={contact.contactId} className="opportunity">
                                                <div className="opportunity-name">
                                                    {contact.firstName} {contact.lastName}
                                                </div>
                                                <ul className="opportunity-details">
                                                    <li>
                                                        <strong>Company:</strong>{" "}
                                                        {company?.companyInfo?.companyName ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Firm Type:</strong> {contact?.firmTypeDesc ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Title:</strong> {contact?.titleDesc ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Contact ID:</strong> {contact?.contactId ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Email:</strong> {contact?.emailAddress ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>LinkedIn:</strong>{" "}
                                                        {contact?.linkedInId ? (
                                                            <a
                                                                href={`https://www.linkedin.com/in/${contact.linkedInId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {contact.linkedInId}
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </li>
                                                    <li>
                                                        <strong>Phone:</strong> +{contact.phone?.cc ?? ""}{" "}
                                                        {contact.phone?.number ?? ""}
                                                    </li>
                                                    {contact.phone?.ext && (
                                                        <li>
                                                            <strong>Ext:</strong> {contact.phone.ext}
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </React.Fragment>
                            );

                        })}

                        {projectInfo?.projectCompaniesSummary?.map((company, index) => {
                            const contacts = company.externalContacts || [];

                            if (contacts.length === 0) return null;

                            return (
                                <React.Fragment key={`internal-${index}`}>
                                    <h2 className="opp-main-heading">External Vendor {projectInfo?.projectCompaniesSummary[0].companyInfo?.companyName} - Contact List</h2>
                                    <div key={index} className="opportunity-container">
                                        {contacts.map((contact) => (
                                            <div key={contact.contactId} className="opportunity">
                                                <div className="opportunity-name">
                                                    {contact.firstName} {contact.lastName}
                                                </div>
                                                <ul className="opportunity-details">
                                                    <li>
                                                        <strong>Company:</strong>{" "}
                                                        {company?.companyInfo?.companyName ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Firm Type:</strong> {contact?.firmTypeDesc ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Title:</strong> {contact?.titleDesc ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Contact ID:</strong> {contact?.contactId ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>Email:</strong> {contact?.emailAddress ?? "N/A"}
                                                    </li>
                                                    <li>
                                                        <strong>LinkedIn:</strong>{" "}
                                                        {contact?.linkedInId ? (
                                                            <a
                                                                href={`https://www.linkedin.com/in/${contact.linkedInId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {contact.linkedInId}
                                                            </a>
                                                        ) : (
                                                            "N/A"
                                                        )}
                                                    </li>
                                                    <li>
                                                        <strong>Phone:</strong> +{contact.phone?.cc ?? ""}{" "}
                                                        {contact.phone?.number ?? ""}
                                                    </li>
                                                    {contact.phone?.ext && (
                                                        <li>
                                                            <strong>Ext:</strong> {contact.phone.ext}
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </>
                ) : (
                    <div style={{ marginTop: "20px" }}>
                        <h6>No project selected or project details not available.</h6>
                    </div>
                )}
            </div>
        </>
    )
}

const Plants = ({ selectedPlant }) => {

    //format plant date 
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const cleaned = dateString.replace(/\[.*\]/, ""); // remove [UTC] or similar
        const date = new Date(cleaned);
        return date.toLocaleDateString("en-US");
    };

    //format plant address
    const getFormattedMailingAddress = (address) =>
        [
            address?.addressLine1,
            address?.city,
            address?.stateName,
            address?.postalCode,
            address?.countyName,
        ]
            .filter(Boolean)
            .join(", ") || "N/A";

    //format plant phonenumber
    const formatPhoneNumber = (phone) =>
        !phone || !phone.number || phone.cc === 0 ? "N/A" : `+${phone.cc} ${phone.number}`;




    //MailingAddress mapped here from the selectedPlant
    const mailingAddress = selectedPlant?.mailingAddress || {};


    return (
        <>
            <div className="opportunity" style={{ maxHeight: "60vh", overflowY: "scroll" }}>
                <h6 style={{ fontSize: "18px", paddingBottom: "6px" }}>Plant Details</h6>
                <div className="detection-item">
                    <p>
                        <span className="left">Plant Name:</span>
                        <span className="right">{selectedPlant?.plantName ?? "N/A"}</span>
                    </p>

                    <p>
                        <span className="left">Number of employees:</span>
                        <span className="right">{selectedPlant?.noEmployees ?? "N/A"}</span>
                    </p>
                    <p>
                        <span className="left">Startup date:</span>
                        <span className="right">{formatDate(selectedPlant?.startupDate)}</span>
                    </p>
                    <p>
                        <span className="left">Close date:</span>
                        <span className="right">{formatDate(selectedPlant?.closeDate)}</span>
                    </p>
                    <p>
                        <span className="left">Existing Sq.Ft.:</span>
                        <span className="right">
                            {selectedPlant?.existingSqFt
                                ? `${Number(selectedPlant.existingSqFt).toLocaleString("en-US")} sq ft`
                                : "N/A"}
                        </span>
                    </p>
                </div>

                <div className="detection-item">
                    <h6 style={{ fontSize: "18px" }}>Mailing Address</h6>
                    <p>
                        <span className="left" style={{ width: '10rem' }}>Address Line 1:</span>
                        <span className="right">{getFormattedMailingAddress(mailingAddress)}</span>
                    </p>
                    <p>
                        <span className="left">Plant Phone:</span>
                        <span className="right">{formatPhoneNumber(selectedPlant?.phone)}</span>
                    </p>

                </div>

                <div className="detection-item">
                    <h6 style={{ fontSize: "18px" }}>Plant Industry Sector</h6>
                    {selectedPlant?.sectors?.length > 0 ? (
                        selectedPlant.sectors.map((sector, index) => (
                            <p key={index}>
                                <span className="left">{sector.plantSectorDesc ?? "N/A"}</span>
                            </p>
                        ))
                    ) : (
                        <p>
                            <span className="left">N/A</span>
                        </p>
                    )}
                </div>

                <div className="detection-item">
                    <h6 style={{ fontSize: "18px" }}>Fuel</h6>
                    <p>
                        <span className="left">Primary Fuel:</span>
                        <span className="right">{selectedPlant?.fuel?.primaryFuel || 'N/A'}</span>
                    </p>
                    <p>
                        <span className="left">Secondary Fuel:</span>
                        <span className="right">{selectedPlant?.fuel?.secondaryFuel || 'N/A'}</span>
                    </p>

                </div>

                <div className="detection-item">

                    <p>
                        <span className="left" style={{ fontSize: "18px", color: 'black' }}>Cogen/CHP:</span>
                        <span className="right">{selectedPlant?.cogenChp || '0'}</span>
                    </p>


                </div>

                <div className="detection-item">
                    <h6 style={{ fontSize: "18px" }}>SIC Product</h6>
                    {selectedPlant?.sicProduct?.length > 0 ? (
                        selectedPlant.sicProduct.map((sector, index) => (
                            <p key={index}>
                                <span className="left">{sector.sicProductDesc ?? "N/A"}</span>
                            </p>
                        ))
                    ) : (
                        <p>
                            <span className="left">N/A</span>
                        </p>
                    )}
                </div>


                <div className="opportunity-container">

                    {selectedPlant?.owner?.map((owner, index) => (
                        <React.Fragment key={index}>
                            <h2 className="opp-main-heading">OWNERS</h2>
                            <div className="opportunity" key={index}>
                                <div className="opportunity-name">
                                    {owner.companyName ?? "N/A"}
                                </div>
                                <ul className="opportunity-details">
                                    <li>
                                        <strong>Classification:</strong>{" "}
                                        {owner.classificationDesc ?? "N/A"}
                                    </li>
                                    <li>
                                        <strong>Ownership percentage:</strong>{" "}
                                        {owner.ownerPercent != null ? `${owner.ownerPercent}%` : "N/A"}
                                    </li>
                                    <li>
                                        <strong>Website:</strong>{" "}
                                        {owner.companyWebsite ? (
                                            <a href={owner.companyWebsite} target="_blank" rel="noopener noreferrer">
                                                {owner.companyWebsite}
                                            </a>
                                        ) : (
                                            "N/A"
                                        )}
                                    </li>
                                </ul>
                            </div>
                        </React.Fragment>
                    ))}


                    {selectedPlant?.parent && (
                        <>
                            <h2 className="opp-main-heading">PARENT</h2>
                            <div className="opportunity">
                                <div className="opportunity-name">
                                    {selectedPlant.parent.companyName ?? "N/A"}
                                </div>
                                <ul className="opportunity-details">
                                    <li>
                                        <strong>Website:</strong>{" "}
                                        {selectedPlant.parent.website ? (
                                            <a
                                                href={
                                                    selectedPlant.parent.website.startsWith("http")
                                                        ? selectedPlant.parent.website
                                                        : `https://${selectedPlant.parent.website}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {selectedPlant.parent.website}
                                            </a>
                                        ) : (
                                            "N/A"
                                        )}
                                    </li>
                                </ul>
                            </div>
                        </>
                    )}


                    {selectedPlant?.capacity?.map((capacity, index) => (
                        <React.Fragment key={index}>
                            <h2 className="opp-main-heading">Capacity</h2>
                            <div className="opportunity" key={index}>
                                <div className="opportunity-name">
                                    {capacity.productDesc ?? "N/A"}
                                </div>
                                <ul className="opportunity-details">
                                    <li>
                                        <strong>Type:</strong>{" "}
                                        {capacity.type ?? "N/A"}
                                    </li>
                                    <li>
                                        <strong>Actual Capacity:</strong>{" "}
                                        {capacity.actualCapacity ?? "N/A"}
                                    </li>
                                    <li>
                                        <strong>Design Capacity:</strong>{" "}
                                        {capacity.designCapacity ?? "N/A"}
                                    </li>
                                    <li>
                                        <strong>Unit of Measures:</strong>{" "}
                                        {capacity.uom ?? "N/A"}
                                    </li>
                                </ul>
                            </div>
                        </React.Fragment>
                    ))}

                </div>
            </div>

        </>
    )
}


const SustainabilityGoals = ({ message, citations, loading }) => {

    return (
        <div>
            {loading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px", // adjust height as per your UI
                    }}
                >
                    <Spin tip="Loading..." />
                </div>
            ) : (
                <>
                    {message && <p>{message}</p>}
                    {citations.length > 0 && (
                        <ul>
                            {citations.map((citation, index) => {
                                if (typeof citation !== "string") return null;
                                const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);

                                if (match) {
                                    const [, label, url] = match;
                                    return (
                                        <li key={index}>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                {label}
                                            </a>
                                        </li>
                                    );
                                }

                                return (
                                    <li key={index}>
                                        <a href={citation} target="_blank" rel="noopener noreferrer">
                                            {citation}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
};


const Contacts = ({ contact }) => {
    const selectedPlant = contact;
    return (
        <>
            {(selectedPlant?.contact?.length > 0 || selectedPlant?.ltsa?.length > 0) && (
                <div className="card" style={{ marginBottom: "16px", maxHeight: "70vh", overflowY: "scroll" }}>

                    {/* IIR Contact */}
                    {selectedPlant?.contact?.length > 0 && (
                        <div className="opportunity-container">
                            <h2 className="opp-main-heading">IIR Plant Contact</h2>
                            {selectedPlant.contact.map((contact, index) => (
                                <div className="opportunity" key={index}>
                                    <div className="opportunity-name">
                                        {contact.firstName} {contact.lastName}
                                    </div>
                                    <ul className="opportunity-details">
                                        <li><strong>Title:</strong> {contact.actualTitle ?? "N/A"}</li>
                                        <li><strong>Functional Title:</strong> {contact.functionalTitle ?? "N/A"}</li>
                                        <li><strong>On-site:</strong> {contact.onSite ?? "N/A"}</li>
                                        <li>
                                            <strong>Email:</strong>{" "}
                                            {contact.emailAddress ? (
                                                <span style={{ color: "#277bff" }}>{contact.emailAddress}</span>
                                            ) : "N/A"}
                                        </li>
                                        <li>
                                            <strong>LinkedIn:</strong>{" "}
                                            {contact.linkedInId ? (
                                                <a
                                                    href={`https://www.linkedin.com/in/${contact.linkedInId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {contact.linkedInId}
                                                </a>
                                            ) : "N/A"}
                                        </li>
                                        <li><strong>Phone Number:</strong> {contact.phone?.number ?? "N/A"}</li>
                                        <li><strong>Ext:</strong> {contact.phone?.ext ?? "N/A"}</li>
                                        <li><strong>Country Code:</strong> {contact.phone?.cc ? `+${contact.phone.cc}` : "N/A"}</li>
                                        <li><strong>Alt Phone:</strong> {contact.altPhone?.number ?? "N/A"}</li>
                                        <li><strong>Alt Ext:</strong> {contact.altPhone?.ext ?? "N/A"}</li>
                                        <li><strong>Alt Country Code:</strong> {contact.altPhone?.cc ? `+${contact.altPhone.cc}` : "N/A"}</li>
                                        <li>
                                            <strong>Alt Phone Number:</strong>{" "}
                                            {contact?.altPhoneMobile && contact.altPhoneMobile !== "0"
                                                ? contact.altPhoneMobile
                                                : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* LTSA Contact */}
                    {selectedPlant?.ltsa?.length > 0 && (
                        <div className="opportunity-container">
                            <h2 className="opp-main-heading">Long Term Service Agreement Contact</h2>
                            {selectedPlant.ltsa.map((item, index) => (
                                <div className="opportunity" key={index}>
                                    <div className="opportunity-name">{item.contractorName ?? "Contractor Name"}</div>
                                    <ul className="opportunity-details">
                                        <li><strong>LTSA ID:</strong> {item.ltsaId ?? "N/A"}</li>
                                        <li><strong>Status Description:</strong> {item.statusDesc ?? "N/A"}</li>
                                        <li><strong>Contract Length:</strong> {item.contractLength ?? "N/A"} {item.contractLength ? "years" : ""}</li>
                                        <li>
                                            <strong>Contract Expiration Date:</strong>{" "}
                                            {item.contractExpiration
                                                ? new Date(item.contractExpiration.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                                                : "N/A"}
                                        </li>
                                        <li><strong>Average Number of Personnel:</strong> {item.personnelCountAvg ?? "N/A"}</li>
                                        <li><strong>Peak Number of Personnel:</strong> {item.personnelCountPeak ?? "N/A"}</li>
                                        <li><strong>LTSA Preferred Contractor:</strong> {item.preferredContractor === "1" ? "Yes" : "No"}</li>
                                        <li>
                                            <strong>QC Date:</strong>{" "}
                                            {item.qcDate
                                                ? new Date(item.qcDate.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                                                : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

export default ProjectDetails;